
import Component from "vue-class-component";
import Vue from "vue";
import "vue-cal/dist/vuecal.css";
import VueCal from "vue-cal";
import "vue-cal/dist/drag-and-drop.es.js";
import { actions, CalendarEntry, Facility, PersonForMeeting } from "@/survey";
import { getPeopleFromStore } from "@/store";
import { getEpochFromDateTime } from "@/views/events";

@Component({
  components: { VueCal }
})
export default class Calendar extends Vue {
  createModalVisible = false;
  savingCreateEvent = false;
  createError = "";
  attendeeFilter = "";
  createAttendees: PersonForMeeting[] = [];
  createEvent: any = this.blankCreateEvent(new Date());

  get selectedDate() {
    return new Date();
  }

  get facilityChoices() {
    return [
      { value: null, text: "Select facility" },
      ...this.$store.state.facilities.map((facility: Facility) => {
        return { value: facility.id, text: facility.name };
      })
    ];
  }

  get people() {
    return getPeopleFromStore();
  }

  get filteredAvailablePeople() {
    const filter = this.attendeeFilter.trim().toLowerCase();
    return this.people.filter((person: PersonForMeeting) => {
      if (this.isAttending(person)) {
        return false;
      }
      if (!filter) {
        return true;
      }
      return `${person.name} ${person.email} ${person.company}`
        .toLowerCase()
        .includes(filter);
    });
  }

  onEventDropped(drop: any) {
    const dt = drop.newDate;
    const eventId = parseInt(drop.event.eventId);
    let existingEvent: any = null;
    this.$store.state.calendar.forEach((e: CalendarEntry) => {
      if (e.id === eventId) {
        existingEvent = { ...e };
      }
    });
    if (!existingEvent || !existingEvent.start || !existingEvent.end) {
      return;
    }
    const duration = existingEvent.end - existingEvent.start;
    const newStart = new Date(existingEvent.start);
    newStart.setDate(dt.getDate());
    newStart.setUTCFullYear(dt.getUTCFullYear());
    newStart.setMonth(dt.getMonth());
    const newEnd = new Date(newStart.getTime() + duration);

    existingEvent.start = newStart.getTime();
    existingEvent.end = newEnd.getTime();
    this.$store.dispatch(actions.CREATE_OR_UPDATE_CALENDAR_ENTRY, {
      toSave: existingEvent,
      success: () => {
        console.log("Saved");
      }
    });
  }

  eventClicked(event: any, e: Event) {
    this.$router.push(`/event/edit/${event.eventId}`);
  }

  openCreateEventModal(value: any) {
    const selectedDate = this.normalizeCalendarDate(value);
    this.createEvent = this.blankCreateEvent(selectedDate);
    this.createAttendees = [];
    this.attendeeFilter = "";
    this.createError = "";
    this.createModalVisible = true;
  }

  resetCreateForm() {
    this.savingCreateEvent = false;
    this.createError = "";
  }

  saveCalendarEvent() {
    this.createError = "";
    if (!this.createEvent.subject || !this.createEvent.subject.trim()) {
      this.createError = "Subject is required.";
      return;
    }
    const start = getEpochFromDateTime(
      this.createEvent.startDate,
      this.withSeconds(this.createEvent.startTime)
    );
    const end = getEpochFromDateTime(
      this.createEvent.endDate,
      this.withSeconds(this.createEvent.endTime)
    );
    if (isNaN(start) || isNaN(end) || end <= start) {
      this.createError = "End date and time must be after start date and time.";
      return;
    }

    const toSave: CalendarEntry = {
      id: null,
      start,
      end,
      subject: this.createEvent.subject.trim(),
      facility: this.createEvent.facility || undefined,
      detail: this.createEvent.detail,
      tableName: "calendar"
    };

    this.savingCreateEvent = true;
    this.$store.dispatch(actions.CREATE_OR_UPDATE_CALENDAR_ENTRY, {
      toSave,
      attendees: this.createAttendees,
      success: () => {
        this.savingCreateEvent = false;
        this.createModalVisible = false;
      }
    });
  }

  addAttendee(person: PersonForMeeting) {
    if (!this.isAttending(person)) {
      this.createAttendees.push(person);
    }
  }

  removeAttendee(person: PersonForMeeting) {
    this.createAttendees = this.createAttendees.filter((attendee: PersonForMeeting) => {
      return this.personKey(attendee) !== this.personKey(person);
    });
  }

  isAttending(person: PersonForMeeting) {
    return this.createAttendees.some((attendee: PersonForMeeting) => {
      return this.personKey(attendee) === this.personKey(person);
    });
  }

  personKey(person: PersonForMeeting) {
    return `${person.type}_${person.id}`;
  }

  normalizeCalendarDate(value: any) {
    const rawDate = value && value.date ? value.date : value;
    return rawDate instanceof Date ? new Date(rawDate.getTime()) : new Date();
  }

  blankCreateEvent(selectedDate: Date) {
    const start = new Date(selectedDate.getTime());
    start.setHours(8, 0, 0, 0);
    const end = new Date(start.getTime());
    end.setHours(9, 0, 0, 0);
    return {
      id: null,
      facility: null,
      subject: "",
      detail: "",
      startDate: this.formatDate(start),
      startTime: this.formatTime(start),
      endDate: this.formatDate(end),
      endTime: this.formatTime(end),
      tableName: "calendar"
    };
  }

  formatDate(date: Date) {
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
  }

  formatTime(date: Date) {
    return `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:00`;
  }

  withSeconds(time: string) {
    return time && time.split(":").length === 2 ? `${time}:00` : time;
  }

  pad(value: number) {
    return value < 10 ? `0${value}` : `${value}`;
  }

  get events() {
    const calendarEvents: any[] = [];
    this.$store.state.calendar.forEach((event: CalendarEntry) => {
      if (event.start === undefined || event.end === undefined) {
        return;
      }
      const start = new Date(event.start);
      calendarEvents.push({
        start,
        end: new Date(event.end),
        title: event.subject || "Untitled Event",
        fullTitle: event.subject || "Untitled Event",
        startTime: this.formatDisplayTime(start),
        class: "bg-success text-white",
        eventId: event.id
      });
    });
    return calendarEvents;
  }

  formatDisplayTime(date: Date) {
    const hours = date.getHours();
    const minutes = this.pad(date.getMinutes());
    const hour12 = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  }
}
